import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, ArrowRight, Loader2, Clock, Users, BookOpen, ShieldCheck } from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader from "../components/app/PageHeader";
import api, { formatApiError } from "../lib/api";

const CATEGORY_ICONS = {
  ifa: Users, mortgage: BookOpen, insurance: ShieldCheck, general: Copy,
};

export default function TemplatesLibrary() {
  const nav = useNavigate();
  const [tab, setTab] = useState("platform");
  const [data, setData] = useState({ platform: [], my_templates: [] });
  const [creating, setCreating] = useState(null);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/api/templates"); setData(data); } catch {}
  })(); }, []);

  const applyTemplate = async (tplKey) => {
    setCreating(tplKey);
    try {
      const { data } = await api.post("/api/flows", { name: `New flow from template`, template: tplKey });
      nav(`/app/flows/${data.id}/edit`);
    } catch (e) { alert(formatApiError(e)); setCreating(null); }
  };

  return (
    <AppShell>
      <PageHeader title="Templates library" subtitle="Start with a regulated template, or save your own flows as reusable templates." />

      <div className="card2 mb-6 inline-flex p-1 rounded-md bg-surface">
        <button onClick={() => setTab("platform")} className={`px-4 py-2 rounded-md t-body-sm font-medium ${tab === "platform" ? "bg-white shadow-card" : "text-slate2"}`} data-testid="tab-platform">Platform templates · {data.platform.length}</button>
        <button onClick={() => setTab("my")} className={`px-4 py-2 rounded-md t-body-sm font-medium ${tab === "my" ? "bg-white shadow-card" : "text-slate2"}`} data-testid="tab-my">My templates · {data.my_templates.length}</button>
      </div>

      {tab === "platform" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.platform.map((t) => {
            const Icon = CATEGORY_ICONS[t.category] || Copy;
            return (
              <div key={t.id} className="card2 card2-hover p-6 flex flex-col" data-testid={`template-${t.template_key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-md bg-indigo2-50 flex items-center justify-center"><Icon size={20} className="text-indigo2" /></div>
                  <span className="badge2 badge-locked">{t.category}</span>
                </div>
                <h3 className="t-h4 mb-2">{t.name}</h3>
                <p className="t-body-sm text-slate2 flex-1 mb-4">{t.description}</p>
                <div className="flex items-center gap-4 t-caption mb-5">
                  <span className="flex items-center gap-1"><Copy size={12} />{t.step_count} steps</span>
                  <span className="flex items-center gap-1"><Clock size={12} />~{t.estimated_minutes} min</span>
                </div>
                <button onClick={() => applyTemplate(t.template_key)} disabled={creating === t.template_key} className="btn btn-primary" data-testid={`use-${t.template_key}`}>
                  {creating === t.template_key ? <Loader2 size={14} className="animate-spin" /> : <>Use template <ArrowRight size={14} /></>}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {tab === "my" && (
        <div className="card2 p-12 text-center">
          <Copy size={32} className="text-muted2 mx-auto mb-3" />
          <h3 className="t-h4 mb-2">No saved templates yet</h3>
          <p className="t-body-sm text-slate2">Save any flow as a template from the flow builder to reuse it across your team.</p>
        </div>
      )}
    </AppShell>
  );
}
